import React, { useState, useRef } from "react";
import { ArrowLeftIcon, Loader2Icon } from "lucide-react";
import {
  PaymentMethodSelector,
  PaymentMethod,
} from "@/components/academician/payment-method-selector";
import { StripeCardForm } from "@/components/academician/stripe-card-form";
import { OrderSummary } from "@/components/academician/order-summary";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { cn } from "@/lib/utils";

interface CheckoutPageProps {
  /**
   * Cohort details
   */
  cohort?: {
    id: string;
    name: string;
    startDate: string;
    duration: string;
    price: number;
  };

  /**
   * User email (pre-filled if logged in)
   */
  userEmail?: string;

  /**
   * Discount code applied
   */
  discountCode?: string;

  /**
   * Discount amount
   */
  discountAmount?: number;

  /**
   * Payment callback
   */
  onPayment?: (data: {
    paymentMethod: PaymentMethod;
    email: string;
    fullName: string;
    country: string;
    agreeToTerms: boolean;
  }) => Promise<void>;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Cohort name (from ERB)
   */
  cohortName?: string;

  /**
   * Cohort price formatted (from ERB)
   */
  cohortPrice?: string;

  /**
   * Cohort start date (from ERB)
   */
  startDate?: string;

  /**
   * Cohort duration in weeks (from ERB)
   */
  durationWeeks?: number;

  /**
   * CSRF token (from ERB)
   */
  csrfToken?: string;

  /**
   * Checkout form action URL (from ERB)
   */
  checkoutUrl?: string;
}

// Default cohort data
const defaultCohort = {
  id: "1",
  name: "Customer Discovery Fundamentals",
  startDate: "March 15, 2024",
  duration: "8 weeks",
  price: 499,
};

// Country list
const countries = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Netherlands",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Ireland",
  "New Zealand",
  "Singapore",
  "Japan",
  "South Korea",
  "India",
  "Brazil",
  "Mexico",
  "Argentina",
  "Other",
];

export function CheckoutPage({
  cohort = defaultCohort,
  userEmail = "",
  discountCode,
  discountAmount = 0,
  onPayment,
  className = "",
  cohortName,
  cohortPrice,
  startDate,
  durationWeeks,
  csrfToken,
  checkoutUrl,
}: CheckoutPageProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");
  const [email, setEmail] = useState(userEmail);
  const [fullName, setFullName] = useState("");
  const [country, setCountry] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [cardError, setCardError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  // Use ERB-provided cohort data if available
  const resolvedCohort = {
    ...cohort,
    name: cohortName || cohort.name,
    startDate: startDate || cohort.startDate,
    duration: durationWeeks ? `${durationWeeks} weeks` : cohort.duration,
    price: cohortPrice ? parseFloat(cohortPrice.replace(/[^0-9.]/g, '')) : cohort.price,
  };
  const resolvedPriceDisplay = cohortPrice || `$${cohort.price}`;

  // Form validation
  const isFormValid =
    email.trim() !== "" &&
    fullName.trim() !== "" &&
    country !== "" &&
    agreeToTerms &&
    (paymentMethod === "paypal" || !cardError);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsProcessing(true);

    // If checkoutUrl is provided (from ERB), submit as a real form POST
    if (checkoutUrl) {
      formRef.current?.submit();
      return;
    }

    try {
      if (onPayment) {
        await onPayment({
          paymentMethod,
          email,
          fullName,
          country,
          agreeToTerms,
        });
      }
    } catch (error) {
      console.error("Payment failed:", error);
      setCardError("Payment failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a
              href="/cohorts"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Cohorts
            </a>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  BL
                </span>
              </div>
              <span className="font-semibold text-foreground">BitLearn</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-7xl mx-auto px-4 py-8">
        <form ref={formRef} onSubmit={handleSubmit} action={checkoutUrl} method="post">
          {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
          <input type="hidden" name="payment_method" value={paymentMethod} />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* LEFT COLUMN - Payment Form (60%) */}
            <div className="lg:col-span-3 space-y-8">
              {/* Page Title */}
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Complete Your Enrollment
                </h1>
                <p className="text-muted-foreground">
                  Secure your spot in {resolvedCohort.name}
                </p>
              </div>

              {/* Payment Method */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  Payment Method
                </h2>
                <PaymentMethodSelector
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                />
              </div>

              {/* Stripe Form */}
              {paymentMethod === "stripe" && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    Card Details
                  </h2>
                  <StripeCardForm error={cardError} loading={isProcessing} />
                </div>
              )}

              {/* PayPal Section */}
              {paymentMethod === "paypal" && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    PayPal Payment
                  </h2>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      You'll be redirected to PayPal to complete your payment
                      securely.
                    </p>
                    {/* PayPal Button Placeholder */}
                    <div className="h-12 bg-[#ffc439] rounded-lg flex items-center justify-center">
                      <span className="text-sm font-semibold text-[#003087]">
                        PayPal Button (Integration Required)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Information */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  Billing Information
                </h2>
                <div className="space-y-4">
                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      className="w-full px-4 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                  </div>

                  {/* Full Name */}
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Doe"
                      required
                      className="w-full px-4 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Country
                    </label>
                    <select
                      id="country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                      className="w-full px-4 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <option value="">Select your country</option>
                      {countries.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Order Summary (40%) */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-8 space-y-6">
                {/* Order Summary */}
                <OrderSummary
                  cohortName={resolvedCohort.name}
                  startDate={resolvedCohort.startDate}
                  duration={resolvedCohort.duration}
                  subtotal={resolvedCohort.price}
                  discount={discountAmount}
                  discountCode={discountCode}
                />

                {/* Terms and Submit */}
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  {/* Terms Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    />

                    <span className="text-sm text-muted-foreground">
                      I agree to the{" "}
                      <a
                        href="/terms"
                        className="text-primary hover:underline"
                        target="_blank"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        className="text-primary hover:underline"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>

                  {/* Submit Button */}
                  <AcademicianStartupButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={!isFormValid || isProcessing}
                    loading={isProcessing}
                    className="w-full"
                  >
                    {isProcessing ? "Processing..." : "Complete Payment"}
                  </AcademicianStartupButton>

                  {/* Security Note */}
                  <p className="text-xs text-center text-muted-foreground">
                    Your payment information is encrypted and secure
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
